import React, {useRef, useState} from 'react';
import {Alert, Box, Button, CircularProgress, Typography} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import {uploadCustomPageImage} from '../../../services/createCustomHtmlService';
import styles from './ImageUploadModal.module.scss';

const MAX_FILES = 3;
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

const extractUploadedUrl = (response) => {
    const data = response?.data;

    if (!data) return '';
    if (typeof data === 'string') return data;

    return (
        data.publicUrl
        || data.url
        || data.fileUrl
        || data.image_url
        || data?.data?.publicUrl
        || data?.data?.url
        || ''
    );
};

const ImageUploadModal = ({open, onClose, onImagesUploaded}) => {
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');

    const resetState = () => {
        setIsDragging(false);
        setIsUploading(false);
        setError('');
    };

    const closeModal = () => {
        resetState();
        onClose();
    };

    const validateFiles = (files) => {
        if (!files || files.length === 0) return 'Please select at least 1 image.';
        if (files.length > MAX_FILES) return `Maximum ${MAX_FILES} files allowed.`;

        for (const file of files) {
            if (!ACCEPTED_TYPES.includes(file.type)) {
                return 'Only JPG, PNG, WEBP, and GIF files are allowed.';
            }
            if (file.size > MAX_FILE_SIZE) {
                return `File size must be under ${MAX_FILE_SIZE_MB}MB.`;
            }
        }

        return '';
    };

    const uploadFiles = async (files) => {
        const validationError = validateFiles(files);
        if (validationError) {
            setError(validationError);
            return;
        }

        setError('');
        setIsUploading(true);

        try {
            const uploadedUrls = [];
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                const response = await uploadCustomPageImage(formData);
                const uploadedUrl = extractUploadedUrl(response);

                if (!uploadedUrl) {
                    throw new Error('Upload succeeded but no public URL returned.');
                }

                uploadedUrls.push(uploadedUrl);
            }

            onImagesUploaded(uploadedUrls);
            closeModal();
        } catch (uploadError) {
            setError(uploadError?.message || 'Image upload failed.');
        } finally {
            setIsUploading(false);
        }
    };

    const onFileInputChange = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        await uploadFiles(selectedFiles);
        event.target.value = '';
    };

    const onDrop = async (event) => {
        event.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(event.dataTransfer.files || []);
        await uploadFiles(droppedFiles);
    };

    if (!open) return null;

    return (
        <Box className={styles.inlinePanel}>
            <Box className={styles.inlineHeader}>
                <Typography variant="subtitle1">Upload Images</Typography>
                <Button size="small" onClick={closeModal}>Close</Button>
            </Box>

            <Box
                className={`${styles.dropzone} ${isDragging ? styles.dragging : ''}`}
                onDragEnter={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDrop={onDrop}
            >
                <CloudUploadOutlinedIcon className={styles.icon}/>
                <Typography variant="h6">Click to upload or drag and drop</Typography>
                <Typography variant="body2">Maximum {MAX_FILES} files, {MAX_FILE_SIZE_MB}MB each.</Typography>

                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    multiple
                    onChange={onFileInputChange}
                    className={styles.hiddenInput}
                />

                <Button
                    variant="contained"
                    onClick={() => inputRef.current?.click()}
                    disabled={isUploading}
                >
                    Choose Files
                </Button>
            </Box>

            {isUploading && (
                <Box className={styles.uploadingRow}>
                    <CircularProgress size={20}/>
                    <Typography variant="body2">Uploading image...</Typography>
                </Box>
            )}

            {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
        </Box>
    );
};

export default ImageUploadModal;
